import { currentTimestamp } from '@ardoq/date-time';
import { sum } from 'lodash';

type Interval = {
  startTime: number;
  endTime: number;
};

let currentHiddenPeriodStart: number | null = null;
export let intervalsWhenHidden: Interval[] = [];

const cleanupOldTimestamps = (now: number) => {
  const cutOffPoint = now - 24 * 60 * 60 * 1000;
  intervalsWhenHidden = intervalsWhenHidden.filter(
    interval => cutOffPoint < interval.endTime
  );
};

export const handleVisibilityStateEvent = (
  visibilityState: DocumentVisibilityState,
  timestamp: number
) => {
  cleanupOldTimestamps(timestamp);

  if (visibilityState === 'hidden') {
    if (!currentHiddenPeriodStart) {
      currentHiddenPeriodStart = timestamp;
    }
  } else if (visibilityState === 'visible') {
    if (currentHiddenPeriodStart) {
      intervalsWhenHidden.push({
        startTime: currentHiddenPeriodStart,
        endTime: timestamp,
      });
      currentHiddenPeriodStart = null;
    }
  }
};

export const initializeImpl = (
  visibilityState: DocumentVisibilityState,
  now: number
) => {
  currentHiddenPeriodStart = visibilityState === 'hidden' ? now : null;

  intervalsWhenHidden = [];
  window.addEventListener('visibilitychange', () => {
    handleVisibilityStateEvent(document.visibilityState, currentTimestamp());
  });
};

export const initialize = () =>
  initializeImpl(document.visibilityState, currentTimestamp());

const intervalIntersection = (a: Interval, b: Interval): Interval | null => {
  const startTime = Math.max(a.startTime, b.startTime);
  const endTime = Math.min(a.endTime, b.endTime);

  if (startTime <= endTime) {
    return { startTime, endTime };
  }

  return null;
};

const intervalDuration = (interval: Interval | null): number => {
  return interval ? interval.endTime - interval.startTime : 0;
};

/**
 * What percentage of transaction time has the document been visible? The most common scenario
 * is user switching between browser tabs. This can influence our measurements as timers are
 * throttled when document is not visible.
 *
 * The result is a float between 0.0 and 1.0.
 */
export const documentVisibilityRatio = (transactionStartTime: number) => {
  const now = currentTimestamp();
  const transactionInterval = {
    startTime: transactionStartTime,
    endTime: now,
  };

  const intervalsWhenHiddenIncludingPending = currentHiddenPeriodStart
    ? [
        ...intervalsWhenHidden,
        {
          startTime: currentHiddenPeriodStart,
          endTime: now,
        },
      ]
    : intervalsWhenHidden;

  const durationHidden = sum(
    intervalsWhenHiddenIncludingPending.map(interval =>
      intervalDuration(intervalIntersection(interval, transactionInterval))
    )
  );

  return 1.0 - durationHidden / intervalDuration(transactionInterval);
};
